if (!customElements.get('ab-hero-carousel')) {
  customElements.define('ab-hero-carousel', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.slides = Array.from(this.querySelectorAll('.ab-hero__slide'));
      this.dots = Array.from(this.querySelectorAll('.ab-hero__dot'));
      this.index = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      this.autoplayMs = Number(this.dataset.autoplay) || 5000;
      this.pauseMs = Number(this.dataset.pause) || 8000;
      this.manualUntil = 0;
      this.paused = false;
      this.visible = true;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.playback = this.querySelector('.ab-hero__playback');
      this.querySelector('.ab-hero__arrow--prev')?.addEventListener('click', () => this.manual(this.index - 1), options);
      this.querySelector('.ab-hero__arrow--next')?.addEventListener('click', () => this.manual(this.index + 1), options);
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.manual(i), options));
      this.playback?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.updatePlayback();
        this.run();
      }, options);
      let startX = null;
      this.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { ...options, passive: true });
      this.addEventListener('touchend', e => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.manual(this.index + (dx < 0 ? 1 : -1));
        startX = null;
      }, { ...options, passive: true });
      this.addEventListener('touchcancel', () => { startX = null; }, options);
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        video.addEventListener('playing', () => {
          if (i !== this.index || !this.canRun()) { video.pause(); return; }
          slide.classList.add('is-playing');
          this.schedule(Math.max(0, video.duration - video.currentTime) * 1000 + 5000);
        }, options);
        video.addEventListener('ended', () => {
          if (i === this.index) this.schedule(0);
        }, options);
        video.addEventListener('error', () => {
          slide.classList.remove('is-playing');
          if (i === this.index) this.schedule(this.autoplayMs);
        }, options);
      });
      document.addEventListener('visibilitychange', () => this.run(), options);
      this.motion.addEventListener('change', () => { this.updatePlayback(); this.run(); }, options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.run();
      });
      this.observer.observe(this);
      this.updatePlayback();
      this.run();
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.events?.abort();
      this.observer?.disconnect();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
    canRun() {
      return this.isConnected && !this.paused && !this.motion.matches && !document.hidden && this.visible;
    }
    updatePlayback() {
      if (!this.playback) return;
      this.playback.hidden = this.motion.matches || (this.slides.length < 2 && !this.querySelector('video'));
      this.playback.textContent = this.paused ? 'Play' : 'Pause';
      this.playback.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
      this.playback.setAttribute('aria-pressed', String(this.paused));
    }
    schedule(delay) {
      clearTimeout(this.timer);
      if (!this.canRun() || this.slides.length < 2) return;
      this.timer = setTimeout(() => this.go(this.index + 1), Math.max(delay, this.manualUntil - Date.now(), 0));
    }
    go(i) {
      clearTimeout(this.timer);
      this.slides[this.index]?.querySelector('video')?.pause();
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, n) => slide.classList.toggle('is-active', n === this.index));
      this.dots.forEach((dot, n) => dot.classList.toggle('is-active', n === this.index));
      const slide = this.slides[this.index];
      const video = slide.querySelector('video');
      // Keep the outgoing last frame during the fade; reset only on re-entry.
      slide.classList.remove('is-playing');
      if (video?.readyState) video.currentTime = 0;
      this.run();
    }
    manual(i) {
      this.manualUntil = Date.now() + this.pauseMs;
      this.go(i);
    }
    run() {
      const runId = this.runId = (this.runId || 0) + 1;
      clearTimeout(this.timer);
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (i !== this.index || !this.canRun()) video?.pause();
        if (this.motion.matches) slide.classList.remove('is-playing');
      });
      if (!this.canRun() || !this.slides.length) return;
      const slide = this.slides[this.index];
      const video = slide.querySelector('video');
      if (!video || video.error) { this.schedule(this.autoplayMs); return; }
      if (video.ended) { this.schedule(0); return; }
      if (!video.hasAttribute('src')) video.src = video.dataset.src;
      video.muted = true;
      // A stalled request must not leave the slideshow stuck on this slide.
      this.schedule(15000);
      video.play().catch(() => {
        if (runId !== this.runId || this.slides[this.index] !== slide || !this.canRun()) return;
        slide.classList.remove('is-playing');
        this.schedule(this.autoplayMs);
      });
    }
  });
}
